import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useContrastedBackground } from 'app/frontend/base/components/Card'
import { useState } from 'react'
import {
  EventOverviewStatusByEventValue,
  useUserStat,
  type UserStatView,
} from '../utils/useUserStat'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { usePageInfo } from 'app/frontend/base/config/queries'
import TeamMemberLink from 'app/frontend/base/components/TeamMemberLink'
import useEventParticipantStatusMutation from './useEventParticipantStatusMutation'
import EventParticipantStatusControl from './EventParticipantStatusControl'

export type EventOverviewUserProps = {
  main: wowaudit.TeamMembership
  visibleEvents: wowaudit.EventReference[]
  statusByEvent: Map<number, EventOverviewStatusByEventValue>
  statView: UserStatView
  hideStats?: boolean
  iconType?: 'both' | 'role'
}

const EventOverviewUser = (props: EventOverviewUserProps) => {
  const {
    main,
    visibleEvents,
    statusByEvent,
    hideStats,
    iconType = 'both',
  } = props
  const {
    data: { user },
  } = usePageInfo()
  const [openDropdownEventId, setOpenDropdownEventId] = useState<number>()
  const [updatingEventId, setUpdatingEventId] = useState<number>()

  const background = useContrastedBackground()
  const { tooltipValue, displayValue } = useUserStat(props)
  const { accessLevel } = useAuth()

  const isOwnCharacter = !!user && user.id === main.characterReference.userId
  const { selectableStatuses, updateStatus, isUpdatingStatus } =
    useEventParticipantStatusMutation(
      {
        characterId: main.characterReference.id,
        role: main.role,
        specId: main.specId ?? undefined,
      },
      {
        onSuccess: () => {
          setOpenDropdownEventId(undefined)
          setUpdatingEventId(undefined)
        },
        onError: () => setUpdatingEventId(undefined),
      },
    )

  return (
    <tr
      className={cn(
        '[&>td]:border-y [&>td]:py-1 [&>td:last-child]:border-r [&>td:last-child]:rounded-r-md',
        background,
      )}
    >
      <td className='border-l rounded-l-md pl-1'>
        <TeamMemberLink member={main} iconType={iconType} />
      </td>

      {!hideStats && (
        <td className='px-2'>
          <Tooltip>
            <TooltipContent>{tooltipValue}</TooltipContent>
            <TooltipTrigger className='w-full text-xs font-normal text-right'>
              {displayValue}
            </TooltipTrigger>
          </Tooltip>
        </td>
      )}

      {visibleEvents.map((event) => {
        const eventStatus = statusByEvent.get(event.id)
        const status = eventStatus?.status ?? 'unknown'
        const participantId = eventStatus?.participantId
        const participant = participantId
          ? event.participantReferences.find(
              (eventParticipant) => eventParticipant.id === participantId,
            )
          : undefined

        const editable =
          accessLevel === 'admin' ||
          (isOwnCharacter && event.status !== 'locked')
        const canEditParticipant = editable
        return (
          <td
            key={event.id}
            className={cn(
              'border-l relative',
              canEditParticipant && 'hover:bg-card transition-colors',
              event.optional ? 'bg-info' : '',
              event.status === 'cancelled' ? 'bg-destructive' : '',
            )}
          >
            <EventParticipantStatusControl
              main={main}
              participant={participant}
              status={status}
              canEditParticipant={canEditParticipant}
              selectableStatuses={selectableStatuses}
              isUpdatingStatus={
                isUpdatingStatus && updatingEventId === event.id
              }
              iconSize='small'
              open={openDropdownEventId === event.id}
              onOpenChange={(open) =>
                setOpenDropdownEventId(open ? event.id : undefined)
              }
              onSelect={(nextStatus) => {
                setUpdatingEventId(event.id)
                updateStatus({
                  eventId: event.id,
                  eventParticipantId: participantId,
                  nextStatus,
                })
              }}
              triggerClassName='focus:outline-none focus:bg-card absolute inset-0 flex items-center justify-center'
            />
          </td>
        )
      })}
    </tr>
  )
}

export default EventOverviewUser
